// pos.js
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import SearchHeader from "./components/SearchHeader";
import ProductSection from "./components/ProductSection";
import CartSidebar from "./components/CartSidebar";
import OutOfStockModal from "./components/OutOfStockModal";
import NoMoreQuantity from "./components/NoMoreQuantity";
import { PosStateSkeleton } from "../../skeleton";

export default function POS() {
  const [loading, setLoading] = useState(true);
  const [cartItems, setCartItems] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isCartVisible, setIsCartVisible] = useState(false);
  const [isOutOfStockModalOpen, setIsOutOfStockModalOpen] = useState(false);
  const [clearSearchTrigger, setClearSearchTrigger] = useState(0);
  const [noMoreQuantity, setNoMoreQuantity] = useState([false, 0, 0]);

  // Initial loading simulation
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      // Reduced delay since we're now using real APIs
      await new Promise((resolve) => setTimeout(resolve, 500));
      setLoading(false);
    };

    loadData();
  }, []);

  // Handle search results
  const handleSearchResults = (results, query) => {
    setSearchResults(results);
    setSearchQuery(query);
  };

  // Clear search results
  const clearSearchResults = () => {
    setSearchResults(null);
    setSearchQuery("");
  };

  // Cart management functions
  const handleUpdateCartQuantity = (productId, condition, quantity, uniqueId = null) => {
    if (quantity <= 0) {
      // Remove item from cart (match by id and condition only, ignore unique_id)
      setCartItems((prev) =>
        prev.filter(
          (item) => !(item.id === productId && item.condition === condition)
        )
      );
    } else {
      // Update existing item in cart and show cart
      setCartItems((prev) => {
        const existingItemIndex = prev.findIndex(
          (item) => item.id === productId && item.condition === condition
        );

        if (existingItemIndex >= 0) {
          const existingItem = prev[existingItemIndex];
          // Check if quantity exceeds available stock
          if (quantity > existingItem.stock) {
            // Don't update if exceeding stock
            setNoMoreQuantity([true, existingItem.stock, existingItem.name]);
            return prev;
          }
          
          // Update existing item
          const updatedItems = [...prev];
          updatedItems[existingItemIndex] = {
            ...updatedItems[existingItemIndex],
            quantity: quantity,
            // Update unique_id if provided (keep the latest one)
            unique_id: uniqueId || updatedItems[existingItemIndex].unique_id,
          };
          // Show cart when updating quantity
          setIsCartVisible(true);
          return updatedItems;
        }
        // If item doesn't exist, we don't add it here - it should be added via handleAddItemToCart
        return prev;
      });
    }
  };

  const handleRemoveFromCart = (productId, condition, uniqueId = null) => {
    // Remove by id and condition only, ignore unique_id
    setCartItems((prev) =>
      prev.filter(
        (item) => !(item.id === productId && item.condition === condition)
      )
    );
  };

  const handleClearCart = () => {
    setCartItems([]);
    setIsCartVisible(false);
  };

  // Auto-hide cart when it becomes empty
  useEffect(() => {
    if (cartItems.length === 0) {
      setIsCartVisible(false);
    }
  }, [cartItems.length]);

  // Handle cart cancel - hide cart without clearing items
  const handleCartCancel = () => {
    setIsCartVisible(false);
  };

  // Handle show cart
  const handleShowCart = () => {
    setIsCartVisible(true);
  };

  // Show cart when items are added
  const handleAddItemToCart = (productData) => {
    setCartItems((prev) => {
      // Match by id and condition only, ignore unique_id for matching
      const existingItemIndex = prev.findIndex(
        (item) =>
          item.id === productData.id && item.condition === productData.condition
      );

      if (existingItemIndex >= 0) {
        // Update existing item (merge into existing)
        const existingItem = prev[existingItemIndex];
        const isDifferentUniqueId = productData.unique_id && 
                                    existingItem.unique_id && 
                                    productData.unique_id !== existingItem.unique_id;
        
        let stockToCheck;
        if (isDifferentUniqueId) {
          // Different unique_id - but in_stock is the total stock for the product (all unique_ids combined)
          // Since in_stock is the same for all unique_ids of the same product, use the new product's stock
          stockToCheck = productData.stock || existingItem.stock || 0;
        } else {
          // Same unique_id or no unique_id - use the new product's stock
          stockToCheck = productData.stock || existingItem.stock;
        }
        
        // Check if new quantity exceeds stock
        if (productData.quantity > stockToCheck) {
          setNoMoreQuantity([true, stockToCheck, existingItem.name]);
          return prev;
        }
        
        const updatedItems = [...prev];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: productData.quantity,
          stock: stockToCheck, // Update stock
          originalStock: isDifferentUniqueId ? stockToCheck : (existingItem.originalStock || existingItem.stock), // Track original combined stock
          // Update unique_id if provided (keep the latest one)
          unique_id: productData.unique_id || updatedItems[existingItemIndex].unique_id,
        };
        // Show cart when updating quantity
        setIsCartVisible(true);
        return updatedItems;
      } else {
        // Add new item - ensure price is a number and include details
        const newItem = {
          ...productData,
          price:
            typeof productData.price === "number"
              ? productData.price
              : parseFloat(productData.price) || 0,
          details: productData.details || "", // Include details field
          unique_id: productData.unique_id || null, // Include unique_id if available
          originalStock: productData.stock || 0, // Track original stock
        };
        // Show cart when adding new item
        setIsCartVisible(true);
        return [...prev, newItem];
      }
    });
  };

  // Handle auto-add from barcode scan
  const handleAutoAddToCart = (productData) => {
    // Check if product is out of stock
    if (!productData.in_stock || productData.in_stock === 0) {
      setIsOutOfStockModalOpen(true);
      clearSearchResults();
      setClearSearchTrigger(prev => prev + 1);
      return;
    }

    // Format product details
    const formatProductDetails = (product) => {
      const details = [];
      if (product.ram) details.push(product.ram);
      if (product.storage) details.push(product.storage);
      // Extract color from variants array
      if (product.variants && product.variants.length > 0 && product.variants[0].Color) {
        details.push(product.variants[0].Color);
      }
      if (product.suffix) details.push(product.suffix);
      if (product.used_mobile !== undefined && product.used_mobile !== null) {
        details.push(product.used_mobile ? "Used" : "New");
      }
      return details.join(" | ");
    };

    const formattedProduct = {
      id: productData.id,
      name: productData.name,
      details: formatProductDetails(productData),
      price: parseFloat(
        productData.avg_selling_price ||
          productData.selling_price ||
          productData.price ||
          0
      ),
      condition: productData.used_mobile ? "Used" : "New",
      stock: productData.in_stock || 0,
      image: productData.image || null,
      quantity: 1, // Auto-add with quantity 1
      unique_id: productData.unique_id, // Store the unique_id
    };

    // Check if item already exists in cart (match by id and condition only, ignore unique_id)
    const existingItemIndex = cartItems.findIndex(
      (item) => item.id === formattedProduct.id && item.condition === formattedProduct.condition
    );

    if (existingItemIndex >= 0) {
      // Item exists - check if we're adding a different unique_id
      const existingItem = cartItems[existingItemIndex];
      const isDifferentUniqueId = formattedProduct.unique_id && 
                                  existingItem.unique_id && 
                                  formattedProduct.unique_id !== existingItem.unique_id;
      
      if (isDifferentUniqueId) {
        // Different unique_id - but in_stock is the total stock for the product (all unique_ids combined)
        // Since in_stock is the same for all unique_ids of the same product, use the new product's stock
        const stockToUse = formattedProduct.stock || existingItem.stock || 0;
        const currentQuantity = existingItem.quantity;
        const newQuantity = currentQuantity + 1;
        
        // Check if new quantity exceeds the product's total stock
        if (newQuantity > stockToUse) {
          setNoMoreQuantity([true, stockToUse, existingItem.name]);
          return;
        }
        
        // Update quantity and stock (use the product's total stock)
        setCartItems((prev) => {
          const updatedItems = [...prev];
          const itemIndex = prev.findIndex(
            (item) => item.id === formattedProduct.id && item.condition === formattedProduct.condition
          );
          if (itemIndex >= 0) {
            updatedItems[itemIndex] = {
              ...updatedItems[itemIndex],
              quantity: newQuantity,
              stock: stockToUse, // Use the product's total stock (not combined)
              originalStock: stockToUse, // Track the total stock
              unique_id: formattedProduct.unique_id, // Update to latest unique_id
            };
            setIsCartVisible(true);
          }
          return updatedItems;
        });
      } else {
        // Same unique_id or no unique_id - increase quantity by 1
        const newQuantity = existingItem.quantity + 1;
        // Use the existing stock (which may be combined from previous merges)
        // or the new product's stock if no existing stock
        const stockToCheck = existingItem.stock || formattedProduct.stock || 0;
        
        // Check if quantity exceeds available stock
        if (newQuantity > stockToCheck) {
          setNoMoreQuantity([true, stockToCheck, existingItem.name]);
          return;
        }
        
        // Update quantity (keep existing stock as it may be combined)
        setCartItems((prev) => {
          const updatedItems = [...prev];
          const itemIndex = prev.findIndex(
            (item) => item.id === formattedProduct.id && item.condition === formattedProduct.condition
          );
          if (itemIndex >= 0) {
            updatedItems[itemIndex] = {
              ...updatedItems[itemIndex],
              quantity: newQuantity,
              stock: stockToCheck, // Keep the existing stock (may be combined)
              originalStock: existingItem.originalStock || stockToCheck, // Preserve original stock tracking
              unique_id: formattedProduct.unique_id || updatedItems[itemIndex].unique_id,
            };
            setIsCartVisible(true);
          }
          return updatedItems;
        });
      }
    } else {
      // Item doesn't exist, add it to cart
      handleAddItemToCart(formattedProduct);
    }
  };


  // Show skeleton while loading
  if (loading) {
    return <PosStateSkeleton currentState="products" />;
  }

  return (
    <main className="pos-main">
      <SearchHeader 
        loading={loading} 
        onSearchResults={handleSearchResults}
        cartItemCount={cartItems.length}
        onShowCart={handleShowCart}
        currentCategory={null}
        currentSubCategory={null}
        onClearSearch={clearSearchResults}
        onAutoAddToCart={handleAutoAddToCart}
        clearSearchTrigger={clearSearchTrigger}
      />

      <div className={`pos-container no-scrollbar ${isCartVisible ? 'with-cart' : ''}`}>
        <div
          className={` ${
            isCartVisible ? "pos-with-cart" : ""
          }`}
        >
          {searchResults ? (
            <ProductSection
              selectedCategory={{ name: "Search Results" }}
              selectedSubCategory={{ name: searchQuery }}
              onBackToCategories={clearSearchResults}
              onBackToSubCategories={clearSearchResults}
              cartItems={cartItems}
              onUpdateCartQuantity={handleUpdateCartQuantity}
              onAddItemToCart={handleAddItemToCart}
              searchResults={searchResults}
              searchQuery={searchQuery}
              isSearchMode={true}
              isCartVisible={isCartVisible}
            />
          ) : (
            <ProductSection
              selectedCategory={{ name: "All Products" }}
              selectedSubCategory={{ name: "All Products" }}
              onBackToCategories={clearSearchResults}
              onBackToSubCategories={clearSearchResults}
              cartItems={cartItems}
              onUpdateCartQuantity={handleUpdateCartQuantity}
              onAddItemToCart={handleAddItemToCart}
              isCartVisible={isCartVisible}
            />
          )}
        </div>
        {isCartVisible && (
          <div className="cart-container">
            <CartSidebar
              cartItems={cartItems}
              onUpdateQuantity={(productId, condition, quantity, uniqueId) => 
                handleUpdateCartQuantity(productId, condition, quantity, uniqueId)
              }
              onRemoveItem={(productId, condition, uniqueId) => 
                handleRemoveFromCart(productId, condition, uniqueId)
              }
              onClearCart={handleClearCart}
              onCancel={handleCartCancel}
              onNavigateToCategories={clearSearchResults}
            />
          </div>
        )}
      </div>

      <OutOfStockModal
        isOpen={isOutOfStockModalOpen}
        onClose={() => setIsOutOfStockModalOpen(false)}
      />
      <NoMoreQuantity
          isOpen={noMoreQuantity}
          onClose={() => setNoMoreQuantity([false, 0, 0])}
        ></NoMoreQuantity>
    </main>
  );
}